export type PublicConfiguration = {
  mapboxApiKey: string;
  pixabayApiKey: string;
  sentryDsn?: string;
  vapidPublicKey?: string;
};

function readRequired(name: 'VITE_MAPBOX_API_KEY' | 'VITE_PIXABAY_API_KEY', value: string | undefined) {
  const normalizedValue = value?.trim();
  if (!normalizedValue) throw new Error(`Missing required public configuration: ${name}. See .env.example.`);
  return normalizedValue;
}

export const publicConfig: PublicConfiguration = {
  mapboxApiKey: import.meta.env.VITE_MAPBOX_API_KEY?.trim() ?? '',
  pixabayApiKey: import.meta.env.VITE_PIXABAY_API_KEY?.trim() ?? '',
  sentryDsn: import.meta.env.VITE_SENTRY_DSN?.trim() || undefined,
  vapidPublicKey: import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim() || undefined,
};

function normalizedPublicConfig(value: unknown): Partial<PublicConfiguration> {
  if (!value || typeof value !== 'object') return {};
  const record = value as Record<string, unknown>;
  const config: Partial<PublicConfiguration> = {};
  for (const key of ['mapboxApiKey', 'pixabayApiKey', 'sentryDsn', 'vapidPublicKey'] as const) {
    if (key in record) config[key] = typeof record[key] === 'string' ? record[key].trim() || undefined : undefined;
  }
  return config;
}

/** Loads database-backed browser configuration, retaining build values as migration/offline fallbacks. */
export async function initializePublicConfig(fetcher: typeof fetch = fetch) {
  try {
    const response = await fetcher('/api/custom/config/public', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return publicConfig;
    const runtime = normalizedPublicConfig(await response.json());
    if (runtime.mapboxApiKey) publicConfig.mapboxApiKey = runtime.mapboxApiKey;
    if (runtime.pixabayApiKey) publicConfig.pixabayApiKey = runtime.pixabayApiKey;
    if ('sentryDsn' in runtime) publicConfig.sentryDsn = runtime.sentryDsn;
    if ('vapidPublicKey' in runtime) publicConfig.vapidPublicKey = runtime.vapidPublicKey;
  } catch {
    // A failed runtime lookup must not prevent the SPA from using its migration fallback.
  }
  return publicConfig;
}

export function validatePublicConfig(config: PublicConfiguration = publicConfig) {
  readRequired('VITE_MAPBOX_API_KEY', config.mapboxApiKey);
  readRequired('VITE_PIXABAY_API_KEY', config.pixabayApiKey);
}
